"use client";

import { useEffect, useState } from "react";
import { Menu, Search, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { CartIcon } from "@/components/shop/CartIcon";
import { ProductsSearch, useWPQueryForSearch } from "@/components/shop/ProductsSearch";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopRailMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-row items-center gap-6">
        <div className="h-4 w-20 bg-muted animate-pulse rounded-lg" />
        <div className="h-4 w-20 bg-muted animate-pulse rounded-lg" />
        <div className="h-4 w-20 bg-muted animate-pulse rounded-lg" />
      </div>;
  }
  return <nav className="flex flex-row items-center gap-8" aria-label="Primary">
      {menuItems.map((item, i) => <div key={item.id} data-index={i} className="flex flex-row items-center gap-3">
          <Link to={item.href || "#"} className="text-foreground text-[11px] uppercase tracking-[0.25em] font-default hover:text-primary transition-colors duration-300 whitespace-nowrap">
            {item.label}
          </Link>
          {i < menuItems.length - 1 && <span aria-hidden="true" className="w-1 h-1 rounded-lg bg-primary/30" />}
        </div>)}
    </nav>;
}
function MobileSheetMenu({
  onItemClick
}: {
  onItemClick: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 p-6">
        <div className="h-10 w-40 bg-muted animate-pulse rounded-lg" />
        <div className="h-10 w-32 bg-muted animate-pulse rounded-lg" />
        <div className="h-10 w-36 bg-muted animate-pulse rounded-lg" />
      </div>;
  }
  return <nav className="flex flex-col gap-6 p-8 pt-16" aria-label="Mobile primary">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} onClick={onItemClick} className="font-default italic text-4xl text-foreground hover:text-primary transition-colors duration-300">
          {item.label}
        </Link>)}
    </nav>;
}
export default function MainNavigationHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [scrollProgress, setScrollProgress] = useState(0);
  const [_wpQuery, _hasSearch] = useWPQueryForSearch(searchValue);
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setScrolled(scrollTop > 20);
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = docHeight > 0 ? scrollTop / docHeight * 100 : 0;
      setScrollProgress(Math.min(100, Math.max(0, progress)));
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="2743" id="main-navigation-header" className={`sticky top-0 left-0 z-50 transition-all duration-500 w-full ${scrolled ? "bg-background/90 backdrop-blur-sm border-b border-border" : "bg-background border-b border-border"}`}>
      {}
      <div className="hidden lg:flex items-center justify-between px-8 py-4 max-w-[1440px] mx-auto w-full">
        <div className="flex items-center gap-12">
          <Link to="/" className="flex items-center justify-center" aria-label="TechWire Mobile home">
            <WvcLogo className="h-7 w-auto" />
          </Link>

          <div className="relative">
            <MenuProvider menu_id="22">
              <DesktopRailMenu />
            </MenuProvider>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 mr-4">
            <div className="w-32 h-px bg-border" aria-hidden="true" />
            <div className="w-1.5 h-1.5 rounded-lg bg-primary transition-all duration-300" style={{
            opacity: scrollProgress / 100
          }} />
          </div>

          <Button variant="ghost" size="icon" onClick={() => setSearchOpen(true)} aria-label="Open search" className="text-foreground hover:text-primary">
            <Search className="size-4" />
          </Button>

          <CartIcon variant="ghost" size="icon" iconSize="size-4" className="text-foreground hover:text-primary rounded-lg inline-flex items-center justify-center size-9" />
        </div>
      </div>

      {}
      {searchOpen && <div className="hidden lg:block fixed inset-0 z-[60] bg-foreground/20 backdrop-blur-sm" onClick={() => setSearchOpen(false)} aria-hidden="true">
          <div className="absolute top-0 right-0 h-screen w-[420px] bg-background border-l border-border shadow-lg p-10 flex flex-col gap-8" onClick={e => e.stopPropagation()} role="dialog" aria-label="Search products">
            <div className="flex items-center justify-between">
              <h2 className="font-default italic text-3xl text-foreground font-bold">
                Search Inventory
              </h2>
              <Button variant="ghost" size="icon" onClick={() => setSearchOpen(false)} aria-label="Close search">
                <X className="size-4" />
              </Button>
            </div>
            <ProductsSearch value={searchValue} onChange={setSearchValue} placeholder="Search smartphones, accessories..." className="w-full" />
            <p className="text-sm text-muted-foreground font-default">
              Find the latest flagship devices and premium mobile gear.
            </p>
          </div>
        </div>}

      {}
      <div className={`lg:hidden flex items-center justify-between px-4 sm:px-6 py-4 transition-all duration-500`}>
        <Link to="/" className="flex items-center" aria-label="TechWire Mobile home">
          <WvcLogo className="h-7 w-auto" />
        </Link>

        <div className="flex items-center gap-1 sm:gap-2">
          <Button variant="ghost" size="icon" onClick={() => setMobileSearchOpen(v => !v)} aria-label="Toggle search" className="text-foreground hover:text-primary">
            {mobileSearchOpen ? <X className="size-4" /> : <Search className="size-4" />}
          </Button>

          <CartIcon variant="ghost" size="icon" iconSize="size-4" className="text-foreground hover:text-primary rounded-lg inline-flex items-center justify-center size-9" />

          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Open menu" className="text-foreground hover:text-primary">
                <Menu className="size-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-background text-foreground border-l border-border w-full sm:max-w-md p-0">
              <div className="flex flex-col h-full">
                <div className="px-8 pt-8">
                  <span className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground font-default">
                    Navigation
                  </span>
                </div>
                <MenuProvider menu_id="22">
                  <MobileSheetMenu onItemClick={() => setMobileMenuOpen(false)} />
                </MenuProvider>
                <div className="mt-auto border-t border-border px-8 py-6">
                  <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-default">
                    TechWire Mobile — Precision Tech.
                  </p>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {mobileSearchOpen && <div className="lg:hidden bg-background border-b border-border px-4 sm:px-6 py-4">
          <ProductsSearch value={searchValue} onChange={setSearchValue} placeholder="Search devices..." className="w-full" />
        </div>}
    </section>;
}